import type { IContentPage, ITag, LOCALE_CODE } from '~~/lib/ContentfulService';
import { isDefined } from '~/utils/guards/isDefined';

/**
 * Extracts topic data (slug and ID) from a ContentPage for GTM DataLayer
 * Uses pre-loaded English topic slugs from topicSlugMapEn (similar to localeSlugMap)
 *
 * @param page - The ContentPage with topic tags
 * @param currentLocale - The current page locale
 * @param topicSlugMapEn - Pre-loaded mapping of topic IDs to English slugs
 * @returns Object with topic slugs and IDs arrays
 */
export function extractTopicData(
  page: IContentPage | undefined,
  currentLocale: LOCALE_CODE,
  topicSlugMapEn: Record<string, string> = {}
): {
  topic_slugs: string[];
  topic_slugs_en: string[];
  topic_ids: string[];
} {
  // Return empty arrays if no page or no tags
  if (!page?.fields?.tags?.length) {
    return { topic_slugs: [], topic_slugs_en: [], topic_ids: [] };
  }

  const topicSlugs: string[] = [];
  const topicSlugsEn: string[] = [];
  const topicIds: string[] = [];

  page.fields.tags.filter(isDefined).forEach((topic: ITag) => {
    if (!topic.sys?.id || !topic.fields?.slug) {
      return;
    }

    let topicSlug: string | undefined;

    if (typeof topic.fields.slug === 'object') {
      // Multi-locale object structure
      const slugObj = topic.fields.slug as Record<string, string>;
      topicSlug = slugObj[currentLocale] || slugObj['en']; // Current locale first, fallback to English
    } else {
      // Direct string value - this is the current locale version
      topicSlug = topic.fields.slug;
    }

    // Only add topic if both ID and slug are valid
    if (topicSlug?.trim()) {
      topicIds.push(topic.sys.id);
      topicSlugs.push(topicSlug.trim());

      // Add English slug from pre-loaded map
      const englishSlug = topicSlugMapEn[topic.sys.id];
      if (englishSlug?.trim()) {
        topicSlugsEn.push(englishSlug.trim());
      }
    }
  });

  return {
    topic_slugs: topicSlugs,
    topic_slugs_en: topicSlugsEn,
    topic_ids: topicIds
  };
}
